import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, X, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // If we have a session_id, we're returning from Stripe
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support if amount was deducted.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setTimeout(() => navigate('/'), 2000);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      toast.error('Failed to verify payment. Please contact support.');
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setLoading(true);
    setSelectedPlan(packageId);

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
              <h2 className="text-xl font-bold">Verifying Payment...</h2>
              <p className="text-sm text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_reporterspeed/artifacts/9jftsuzl_StenoU%20Skool.png" 
            alt="StenoU" 
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-lg text-gray-600">
            Select the perfect subscription plan for your court reporting journey
          </p>
        </div>

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <Card className="relative hover:shadow-xl transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start mb-2">
                <CardTitle className="text-2xl">Monthly</CardTitle>
                <Badge variant="outline">Most Flexible</Badge>
              </div>
              <CardDescription className="text-lg">
                Perfect for trying out the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold">$10</span>
                  <span className="text-gray-500">/ month</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Billed monthly, cancel anytime</p>
              </div>

              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Access to all lessons</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Audio practice files</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Progress tracking</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Notes & reminders</span>
                </li>
              </ul>

              <Button
                onClick={() => handleSubscribe('monthly')}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {loading && selectedPlan === 'monthly' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe Monthly
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card className="relative hover:shadow-xl transition-shadow border-2 border-green-500">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-green-600">SAVE $20</Badge>
            </div>
            <CardHeader>
              <div className="flex justify-between items-start mb-2">
                <CardTitle className="text-2xl">Yearly</CardTitle>
                <Badge variant="outline" className="bg-green-50">Best Value</Badge>
              </div>
              <CardDescription className="text-lg">
                Save 17% with annual billing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold">$100</span>
                  <span className="text-gray-500">/ year</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="line-through text-gray-400">$120/year</span> - Save $20
                </p>
              </div>

              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">Everything in Monthly, plus:</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Save $20 per year</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Early access to new features</span>
                </li>
              </ul>

              <Button
                onClick={() => handleSubscribe('yearly')}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                {loading && selectedPlan === 'yearly' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe Yearly
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <Alert className="max-w-4xl mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Secure Payment:</strong> All payments are processed securely through Stripe. 
            We never store your credit card information.
          </AlertDescription>
        </Alert>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Button variant="ghost" onClick={() => navigate('/')}>
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
